import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Plus, Pencil, Trash2, UtensilsCrossed, ChevronDown, ImagePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { compressImage } from "@/lib/imageUtils";
import FiscalFields from "@/components/FiscalFields";

// Cores das categorias
const TIPO_COLORS = {
  pizza:       "bg-orange-900/50 text-orange-100",
  hot_dog:     "bg-fuchsia-900/50 text-fuchsia-100",
  macarronada: "bg-green-900/50 text-green-100",
  lasanha:     "bg-red-900/50 text-red-100",
  bebida:      "bg-purple-900/50 text-purple-100",
};

// Nomes bonitos para as categorias
const TIPO_LABELS = {
  pizza:       "Pizza",
  hot_dog:     "Hot-dog",
  macarronada: "Macarronada",
  lasanha:     "Lasanha",
  bebida:      "Bebida",
};

// Ordem exata exigida para as gavetas
const CATEGORY_ORDER = ["pizza", "hot_dog", "macarronada", "lasanha", "bebida"];

function fmtBRL(v) {
  const num = parseFloat(v) || 0;
  return `R$ ${num.toFixed(2).replace(".", ",")}`;
}

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const[editing, setEditing] = useState(null);
  
  // Adicionado o campo Ingredientes no estado inicial
  const [form, setForm] = useState({
    tipo: "pizza",
    nome_item: "",
    disponivel: true,
    valor: "",
    Ingredientes: "",
    tamanho: "",
  });
  const [saving, setSaving] = useState(false);

  // Upload de imagem
  const fileInputRef = useRef(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Controle das Gavetas (Accordion)
  const [expandedCategory, setExpandedCategory] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get("/menu-items");
      setItems(res.data);
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  }, []);

  useEffect(() => { 
    fetchItems(); 
  },[fetchItems]);

  // 1. Agrupa os itens por categoria e ORDENA POR ID (1, 2, 3...)
  const itemsByTipo = useMemo(() => {
    const map = {};
    const sortedItems = [...items].sort((a, b) => a.id - b.id);

    sortedItems.forEach(item => {
      if (!map[item.tipo]) map[item.tipo] = [];
      map[item.tipo].push(item);
    });
    
    return map;
  }, [items]);

  // 2. Ordena as categorias (gavetas)
  const sortedCategories = useMemo(() => {
    const availableTypes = Object.keys(itemsByTipo);
    return availableTypes.sort((a, b) => {
      const indexA = CATEGORY_ORDER.indexOf(a);
      const indexB = CATEGORY_ORDER.indexOf(b);
      
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      
      return indexA - indexB;
    });
  }, [itemsByTipo]);

  const openNew = () => {
    setEditing(null);
    setForm({ tipo: CATEGORY_ORDER[0], nome_item: "", disponivel: true, valor: "", Ingredientes: "", tamanho: "" });
    setImageFile(null);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      tipo: item.tipo || "",
      nome_item: item.nome_item || "",
      disponivel: item.disponivel ?? true,
      valor: String(item.valor || ""),
      Ingredientes: item.Ingredientes || "",
      tamanho: item.tamanho || "",
      fiscal: item.fiscal || null,
    });
    setImageFile(null);
    setImagePreview(item.imagem_url || null);
    setDialogOpen(true);
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
    } catch {
      alert("Erro ao processar imagem. Tente outro arquivo.");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let savedItem;
      if (editing) {
        const res = await api.put(`/menu-items/${editing.id}`, form);
        savedItem = res.data;
      } else {
        const res = await api.post("/menu-items", form);
        savedItem = res.data;
      }

      // Upload de imagem (se selecionada)
      if (imageFile && savedItem?.id) {
        setUploadingImage(true);
        try {
          const formData = new FormData();
          formData.append("file", imageFile, `item-${savedItem.id}.webp`);
          await api.post(`/menu-items/${savedItem.id}/image`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch {
          alert("Item salvo, mas falha no upload da imagem. Tente novamente editando o item.");
        } finally {
          setUploadingImage(false);
        }
      }

      setDialogOpen(false);
      setImageFile(null);
      setImagePreview(null);
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este item do cardápio?")) return;
    try { 
      await api.delete(`/menu-items/${id}`); 
      fetchItems(); 
    } catch { 
      alert("Erro ao remover"); 
    }
  };

  const toggleDisponivel = async (item) => {
    const newValue = !item.disponivel;
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, disponivel: newValue } : i));
    try {
      await api.put(`/menu-items/${item.id}`, { disponivel: newValue });
    } catch {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, disponivel: item.disponivel } : i));
      alert("Erro ao atualizar disponibilidade");
    }
  };

  return (
    <div className="space-y-6">
      {/* Título da Página */}
      <div className="animate-fade-in-up stagger-1">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Cardápio
        </h1>
        <p className="text-muted-foreground mt-1">Gerencie os itens e categorias do seu menu</p>
      </div>

      {/* Cabeçalho de Ações */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-fade-in-up stagger-2">
        <p className="text-sm text-muted-foreground">{items.length} itens cadastrados</p>
        <Button onClick={openNew} size="sm" className="bg-primary hover:bg-primary/90 text-white gap-1.5 shadow-lg shadow-primary/20">
          <Plus className="w-3.5 h-3.5" /> Novo Item
        </Button>
      </div>

      {/* Gavetas de Categorias */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-card border border-border rounded-lg animate-pulse" />
          ))}
        </div>
      ) : sortedCategories.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground bg-card border border-border rounded-lg animate-fade-in-up stagger-3">
          <UtensilsCrossed className="w-10 h-10 mx-auto mb-3 opacity-30" />
          Nenhum item encontrado no cardápio
        </div>
      ) : (
        <div className="space-y-3 animate-fade-in-up stagger-3">
          {sortedCategories.map((tipo) => {
            const isExpanded = expandedCategory === tipo;
            const categoryItems = itemsByTipo[tipo];
            const count = categoryItems.length;

            return (
              <div key={tipo} className="bg-card border border-border rounded-lg overflow-hidden card-shadow">
                {/* Botão da Gaveta */}
                <button 
                  onClick={() => setExpandedCategory(isExpanded ? null : tipo)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center ${TIPO_COLORS[tipo] || "bg-secondary"}`}>
                      <UtensilsCrossed className="w-4 h-4" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm capitalize">{TIPO_LABELS[tipo] || tipo}</p>
                      <p className="text-xs text-muted-foreground">{count} item{count !== 1 ? "s" : ""}</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                </button>

                {/* Conteúdo da Gaveta (Tabela de Itens) */}
                {isExpanded && (
                  <div className="border-t border-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/50 hover:bg-secondary/50">
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground w-16">ID</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground w-12"></TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome do Item</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground hidden md:table-cell">Ingredientes</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Valor</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-center">Disponível</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {categoryItems.map((item) => (
                          <TableRow key={item.id} className="border-b border-border hover:bg-white/[0.02] transition-colors group">
                            <TableCell className="text-xs text-muted-foreground">{item.id}</TableCell>
                            <TableCell className="py-1">
                              {item.imagem_url ? (
                                <img src={item.imagem_url} alt="" className="w-8 h-8 rounded object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded bg-secondary/30 flex items-center justify-center">
                                  <UtensilsCrossed className="w-3 h-3 text-muted-foreground/30" />
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="font-medium text-sm">
                              {item.nome_item}
                              <span className="block text-xs text-muted-foreground">{item.fiscal?.validado ? "Fiscal conferido" : "Fiscal pendente"}</span>
                              {item.tamanho && (
                                <span className="ml-2 text-[10px] font-semibold text-primary/60 uppercase tracking-wide">{item.tamanho}</span>
                              )}
                            </TableCell>
                            
                            {/* Célula do Nome + Ingredientes */}
                          <TableCell className="hidden md:table-cell">
                            <p className="text-xs text-muted-foreground truncate max-w-[200px] whitespace-pre-line leading-tight">
                            {item.Ingredientes || "-"}
                            </p>
                          </TableCell>

                            <TableCell className="text-sm font-bold text-accent">{fmtBRL(item.valor)}</TableCell>
                            <TableCell className="text-center">
                              <Switch
                                checked={item.disponivel}
                                onCheckedChange={() => toggleDisponivel(item)}
                                className="mx-auto"
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => openEdit(item)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(item.id)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {editing ? "Editar Item" : "Novo Item"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Nome do Item</Label>
              <Input value={form.nome_item}
                onChange={(e) => setForm({ ...form, nome_item: e.target.value })}
                className="bg-input border-transparent focus:border-primary h-10" />
            </div>
            
            {/* NOVO CAMPO: Ingredientes */}
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Ingredientes (Opcional)</Label>
              <textarea 
                value={form.Ingredientes}
                onChange={(e) => setForm({ ...form, Ingredientes: e.target.value })}
                className="w-full bg-input border-transparent focus:border-primary rounded-md p-3 text-sm text-foreground min-h-[60px] resize-y focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Ex: Molho, mussarela, calabresa e cebola" 
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">Tipo (Categoria)</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="bg-input border-transparent h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {CATEGORY_ORDER.map((t) => (
                      <SelectItem key={t} value={t}>{TIPO_LABELS[t] || t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">Valor (R$)</Label>
                <Input value={form.valor}
                  onChange={(e) => setForm({ ...form, valor: e.target.value })}
                  className="bg-input border-transparent focus:border-primary h-10" placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Tamanho (Opcional)</Label>
              <Input
                value={form.tamanho}
                onChange={(e) => setForm({ ...form, tamanho: e.target.value })}
                className="bg-input border-transparent focus:border-primary h-10"
                placeholder="Ex: Família, Dobrada, Tradicional, Para 2 pessoas" />
            </div>

            <FiscalFields value={form.fiscal} onChange={fiscal => setForm({ ...form, fiscal })} />
            {/* Upload de Imagem */}
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Imagem (Opcional)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleImageSelect}
              />
              <div className="flex items-center gap-3">
                {imagePreview ? (
                  <div className="relative w-16 h-16 rounded-md overflow-hidden border border-border bg-secondary/30 shrink-0">
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    {imageFile && (
                      <span className="absolute top-0 right-0 bg-primary text-[9px] text-white px-1 rounded-bl">Nova</span>
                    )}
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-md border border-dashed border-border bg-secondary/20 flex items-center justify-center shrink-0">
                    <ImagePlus className="w-5 h-5 text-muted-foreground/40" />
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Enviando...</>
                  ) : imagePreview ? "Trocar Imagem" : "Escolher Imagem"}
                </Button>
                {imageFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground"
                    onClick={() => { setImageFile(null); setImagePreview(editing?.imagem_url || null); }}
                  >
                    Desfazer
                  </Button>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label className="text-sm text-muted-foreground">Disponível para Venda</Label>
              <Switch checked={form.disponivel}
                onCheckedChange={(v) => setForm({ ...form, disponivel: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}
              disabled={saving || !form.nome_item || !form.tipo}
              className="bg-primary hover:bg-primary/90 text-white">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
